import { IAction } from '../actions/types'
import { IImageAsset } from '../modules/images/types'
import { ITimeline } from '../modules/timeline/types'
import { IVideoInternalAsset } from '../modules/videos/types'

export enum QuestionType {
  RADIO_BUTTONS = 'rb',
  CHECKBOXES = 'cb',
  COMBOBOX = 'scx',
  CASCADE = 'cx',
  REORDER = 'ro',
  INSTRUCTION = 'it',
  IMAGE = 'im',
  VIDEO = 'vo',
  SLIDERS = 'mx',
  PROGRESSIVE_MATRIX = 'mp',
  STAR_RATING = 'sr',
  EMOTICONS_RATING = 'se',
  DISTRIBUTE = 'ps',
  OPEN_ENDED = 'oe',
  FORMS = 'oex',
  IMAGE_RESPONSE = 'ir',
  VIDEO_RESPONSE = 'va',
  VIDEO_RESPONSE_PREQUAL = 'va_pre',
  POLARITY_SCALE = 'psc',
  SIDE_BY_SIDE = 'aro',
  HEATMAP = 'hm',
  RAPID_ASSOCIATION = 'sft',
  IMPRESSION_DIAL = 'emd',
  MAXDIFF = 'md',
  VAN_KONAN = 'vk',
  TOPOGRAPHY = 'bpm',
  QUADRANT_ANALYSIS = 'qa',
  ECOMMERCE = 'ec',
  CONJOINT = 'cjd',
  CONVERSATION = 'cn',
  CONJOINT_EXPRESS = 'cjc',
  SHELF_TEST = 'sh',
  AD_TESTING = 'ad',
  MENU = 'mn',
  COMPOSITE = 'comp',
  FEEDBACK = 'feedback',
  VOID = 'void'
}

export const QuestionTypeIcons: Record<QuestionType, string> = {
  [QuestionType.RADIO_BUTTONS]: 'icon-aytmfd-afdradio-full',
  [QuestionType.CHECKBOXES]: 'icon-aytmfd-afdcheckbox-full',
  [QuestionType.COMBOBOX]: 'icon-aytmfd-qcombobox',
  [QuestionType.CASCADE]: 'icon-aytmfd-qcascade',
  [QuestionType.REORDER]: 'icon-aytmfd-qreorder',
  [QuestionType.INSTRUCTION]: 'icon-aytmfd-qinstruction',
  [QuestionType.IMAGE]: 'icon-aytmfd-qimage',
  [QuestionType.VIDEO]: 'icon-aytmfd-qvideo',
  [QuestionType.SLIDERS]: 'icon-aytmfd-qsliders',
  [QuestionType.PROGRESSIVE_MATRIX]: 'icon-aytmfd-qmatrix',
  [QuestionType.STAR_RATING]: 'icon-aytmfd-qstar',
  [QuestionType.EMOTICONS_RATING]: 'icon-aytmfd-qsmiley',
  [QuestionType.DISTRIBUTE]: 'icon-aytmfd-qdistribute',
  [QuestionType.OPEN_ENDED]: 'icon-aytmfd-qopenended',
  [QuestionType.FORMS]: 'icon-aytmfd-qoeform',
  [QuestionType.IMAGE_RESPONSE]: 'icon-aytmfd-qphoto',
  [QuestionType.VIDEO_RESPONSE]: 'icon-aytmfd-qvideoresponse',
  [QuestionType.VIDEO_RESPONSE_PREQUAL]: 'icon-aytmfd-qvideoresponse',
  [QuestionType.POLARITY_SCALE]: 'icon-aytmfd-qhdsliders',
  [QuestionType.SIDE_BY_SIDE]: 'icon-aytmfd-qdescretechoice',
  [QuestionType.HEATMAP]: 'icon-aytmfd-heatmap',
  [QuestionType.RAPID_ASSOCIATION]: 'icon-aytmfd-qspeeded-fit-test',
  [QuestionType.IMPRESSION_DIAL]: 'icon-aytmfd-emotional-dial',
  [QuestionType.MAXDIFF]: 'icon-aytmfd-qmaxdiff',
  [QuestionType.VAN_KONAN]: 'icon-aytmfd-qpricesens',
  [QuestionType.TOPOGRAPHY]: 'icon-aytmfd-3d-perceptual-map',
  [QuestionType.QUADRANT_ANALYSIS]: 'icon-aytmfd-qperceptualmap',
  [QuestionType.CONJOINT]: 'icon-aytmfd-conjoint',
  [QuestionType.CONJOINT_EXPRESS]: 'icon-aytmfd-conjoint',
  [QuestionType.SHELF_TEST]: 'icon-aytmfd-qshelf-test',
  [QuestionType.CONVERSATION]: 'icon-aytmfd-conversation',
  [QuestionType.AD_TESTING]: 'icon-aytmfd-qad-testing',
  [QuestionType.ECOMMERCE]: 'icon-aytmfd-qecommerce',
  [QuestionType.MENU]: 'icon-aytmfd-qmenu',
  [QuestionType.COMPOSITE]: '',
  [QuestionType.FEEDBACK]: '',
  [QuestionType.VOID]: ''
}

export type QuestionID = string
export type AnswerID = string
export type SubQuestionID = string
export type EntityID = string
export type QuestionSubmitResponse = unknown
export type QuestionCheckResponse = unknown
export type QuestionLog = unknown[]

export type ChatMessage = {
  job_id: string
  status: string
  type: 'llm' | 'respondent'
  text?: string
  id?: AnswerID
  blob?: Blob
}
export interface IQuestionSchema {
  id: QuestionID
  type: QuestionType
  text: string
  image?: IImageAsset
  messages?: ChatMessage[]
  videos?: IVideoInternalAsset[]
  media_mask?: string[]
  position: number
  time: number
}

export interface IResponseValidation {
  valid: boolean
}

export type QuestionViewFrame = number | string

export type QuestionNavigationFrame = [number, number]

export interface QuestionModelTypes {
  schema: IQuestionSchema
  response: unknown
  checkResponse?: QuestionCheckResponse
  responseValidation: IResponseValidation
  submitResponse: QuestionSubmitResponse
}
export interface IQuestionModel<T extends QuestionModelTypes = QuestionModelTypes> {
  get id(): QuestionID
  get type(): QuestionType
  get schema(): T['schema']
  get action(): IAction
  get response(): T['response']
  set response(response: T['response'])
  get responseValidation(): T['responseValidation']
  get checkResponse(): T['checkResponse']
  get submitResponse(): T['submitResponse']
  get frame(): QuestionViewFrame
  set frame(f: QuestionViewFrame)
  nextFrame(): boolean
  prevFrame(): boolean
  get timeline(): ITimeline
  get preloadImages(): string[]
  get questionTime(): number
  get screenNumber(): number
  set screenNumber(screenNumber: number)
  nextScreenNumber(): number
  prevScreenNumber(): number
  get navigationFrame(): QuestionNavigationFrame
  set navigationFrame(navigationFrame: QuestionNavigationFrame)
}

export interface IQuestionModelState<T extends QuestionModelTypes = QuestionModelTypes> {
  questionSchema: T['schema']
  action: IAction
  response: T['response']
  checkResponse?: T['checkResponse']
  responseValidation: T['responseValidation']
  frame: QuestionViewFrame
  timeline: ITimeline
  screenNumber: number
  navigationFrame?: QuestionNavigationFrame
}

export interface IQuestionModelConstructor<
  T extends QuestionModelTypes = QuestionModelTypes,
  ModelType = IQuestionModel<T>
> {
  (questionSchema: T['schema'], action: IAction): ModelType
}

export interface IQuestionComponentProps<
  T extends QuestionModelTypes = QuestionModelTypes,
  ModelType = IQuestionModel<T>
> {
  model: ModelType
}

export interface IQuestionComponentRef {
  /**
   * @returns {boolean} true if next press is processed by question component (for example, switched frame), false otherwise
   */
  tryNext(): boolean
  /**
   * @returns {boolean} true if next prev is processed by question component (for example, switched frame), false otherwise (for example when there is no frames to come back to)
   */
  tryPrev(): boolean
  /**
   * Show validation errors, executed on disabled next button press
   */
  showValidation?(): void
}

export interface IQuestionViewProps<T extends QuestionModelTypes = QuestionModelTypes> {
  schema: T['schema']
  response?: T['response']
  responseValidation: T['responseValidation']
  frame?: QuestionViewFrame
  timeline?: ITimeline
  showValidation?: boolean
  onUpdateResponse?: (response: T['response']) => void
  onDisabledClick?: () => void
  onViewed?: () => void
  frameFullscreenHeight?: number
}
